// CRM statuses v2 (2026-10-16): the stage strip as group tabs — To schedule /
// First visit booked / In treatment / Closed — with small sub-labels inside a
// tab and chips that wrap; empty statuses are faded. Rendered ONLY with
// STATUS_NAMES_V2 on (see lib/stagesV2.js); the server filter is still one
// stage id, so a tab opens its chips and a chip filters.
import { useState } from "react";
import { RefreshCw, X } from "lucide-react";

export default function StageTabsV2({ model, stageCounts, stageFilter, setStageFilter, continuingView,
  setContinuingView, continuingCount, continuingClosedCount = 0, showClosedContinuing, setShowClosedContinuing }) {
  const selected = Number(stageFilter) || null;
  const groupOf = (id) => (model.byId[id] || {}).group;
  const [openGroup, setOpenGroup] = useState((selected && groupOf(selected)) || "Contact");
  const count = (ids) => ids.reduce((acc, id) => acc + (stageCounts[id] || 0), 0);

  return (
    <div data-testid="stage-tabs-v2" className="px-8 py-3 border-b border-slate-100 bg-white">
      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="v2-continuing-view-btn"
          onClick={() => setContinuingView(!continuingView)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold uppercase tracking-wide transition-colors ${
            continuingView ? "border-teal-500 bg-teal-600 text-white shadow-sm" : "border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
          }`}
        >
          <RefreshCw size={12} /> Continuing
          <span className="px-1.5 py-0.5 rounded-full text-xs font-bold normal-case">{continuingCount}</span>
        </button>
        {continuingView && (
          <button
            type="button"
            data-testid="v2-continuing-show-closed-toggle"
            aria-pressed={showClosedContinuing}
            onClick={() => setShowClosedContinuing(!showClosedContinuing)}
            className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-semibold border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
          >
            {showClosedContinuing ? "Showing closed" : "Show closed"}
            <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700">{continuingClosedCount}</span>
          </button>
        )}
        <div className="h-6 w-px bg-slate-300 mx-1" />
        {model.groups.map((g) => {
          const ids = g.stages.map((s) => s.stage_id);
          const isOpen = openGroup === g.key;
          const holdsFilter = !continuingView && selected && ids.includes(selected);
          return (
            <button
              key={g.key}
              data-testid={`stage-tab-${g.key}`}
              onClick={() => setOpenGroup(g.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                isOpen || holdsFilter ? "border-blue-400 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {g.label}
              <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700">{count(ids)}</span>
            </button>
          );
        })}
        {stageFilter !== "" && (
          <button data-testid="v2-clear-stage-filter" onClick={() => setStageFilter("")}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2">
            <X size={12} /> Clear
          </button>
        )}
      </div>
      {model.groups.filter((g) => g.key === openGroup).map((g) => (
        <div key={g.key} data-testid={`stage-tab-panel-${g.key}`} className="mt-2 flex flex-wrap items-start gap-x-5 gap-y-2">
          {g.subs.map((sub) => (
            <div key={sub.label || "main"} className="flex flex-wrap items-center gap-1.5">
              {sub.label && <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mr-1">{sub.label}</span>}
              {sub.stages.map((s) => {
                const st = model.styleFor(s.stage_id);
                const cnt = stageCounts[s.stage_id] || 0;
                const isOn = !continuingView && selected === s.stage_id;
                return (
                  <button
                    key={s.stage_id}
                    data-testid={`v2-stage-chip-${s.stage_id}`}
                    onClick={() => setStageFilter(isOn ? "" : s.stage_id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      isOn ? "border-blue-400 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    } ${cnt === 0 && !isOn ? "opacity-50" : ""}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                    {s.display_v2}
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${st.bg} ${st.text}`}>{cnt}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
